import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RecentLeadMatch {
  id: string;
  lead_number: string;
  customer_name: string;
  status: string;
  lead_entry_type: string | null;
  created_at: string;
  campaign: { name: string } | null;
}

const last9 = (phone: string) => phone.replace(/\D/g, '').slice(-9);

/**
 * Leads from the same phone number in the last N days.
 *
 * Used by the manual lead form: when a CSR keys in a number that already rang
 * in through call tracking, they should convert that lead (which carries the
 * campaign) rather than create a second, untagged one. The audit on 2026-09-08
 * found 64 manual leads in 90 days that duplicated a call lead this way.
 */
export function useRecentLeadsByPhone(phone: string | undefined, opts?: { excludeLeadId?: string; days?: number }) {
  const key = phone ? last9(phone) : '';
  const days = opts?.days ?? 7;

  return useQuery({
    queryKey: ['recent-leads-by-phone', key, opts?.excludeLeadId, days],
    enabled: key.length >= 8,
    staleTime: 30_000,
    queryFn: async (): Promise<RecentLeadMatch[]> => {
      const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
      let query = supabase
        .from('leads')
        .select('id, lead_number, customer_name, status, lead_entry_type, created_at, campaign:campaigns(name)')
        .eq('phone_last9', key)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(5);
      if (opts?.excludeLeadId) query = query.neq('id', opts.excludeLeadId);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as RecentLeadMatch[];
    },
  });
}
